import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Image,
  ImageBackground,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { formatDistance, calculateDistance } from '../utils/googlePlaces';
import { useHousePickDistanceUnit } from '../hooks/useHousePickDistanceUnit';
import { useGooglePlaceDetails } from '../hooks/useGooglePlaceDetails';
import { useGooglePlaceDisplay } from '../hooks/useGooglePlaceDisplay';
import { fontFamily } from '../constants/fonts';
import { colors } from '../constants/theme';

const GP = {
  brown: '#6B4C3B',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
};

/**
 * Get place type display name
 * @param {Array} types - Array of place types
 * @returns {string} Display name for primary type
 */
const getPlaceTypeDisplay = (types = []) => {
  if (!types || types.length === 0) return 'Restaurant';

  // Map Google place types to user-friendly names
  const typeMap = {
    'restaurant': 'Restaurant',
    'food': 'Food',
    'meal_takeaway': 'Takeaway',
    'meal_delivery': 'Delivery',
    'cafe': 'Cafe',
    'bar': 'Bar',
    'bakery': 'Bakery',
    'fast_food_restaurant': 'Fast Food',
    'pizza_place': 'Pizza',
    'coffee_shop': 'Coffee Shop'
  };

  const primaryType = types.find(type => typeMap[type]) || types[0];
  return typeMap[primaryType] || primaryType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Get price level display
 * @param {number} priceLevel - Price level (0-4)
 * @returns {string} Price level display
 */
const getPriceLevelDisplay = (priceLevel) => {
  if (priceLevel === null || priceLevel === undefined) return '';

  const levels = ['', '$', '$$', '$$$', '$$$$'];
  return levels[priceLevel] || '';
};

const getPriceDisplay = (item) => {
  if (item.price) return item.price;
  return getPriceLevelDisplay(item.priceLevel);
};

/**
 * Thumbnail matching explore list: Google photo when available, else partner DB image,
 * otherwise non-partner membership card.
 */
const PlaceResultThumbnail = ({ item, photoUrl }) => {
  const dbImageUri = item.thumbnail || item.imgUrl;
  const listImageUri = photoUrl || (item.isPartner ? dbImageUri : null);

  return (
    <View style={{ position: 'relative' }}>
      {listImageUri ? (
        <Image
          source={{ uri: listImageUri }}
          style={{ width: 60, height: 60, borderRadius: 8 }}
          loadingIndicatorSource={require('../assets/icon.png')}
          fadeDuration={200}
          resizeMode="cover"
        />
      ) : (
        <ImageBackground
          source={require('../assets/nonDisgoPartnerMembershipCard.jpg')}
          style={{ width: 60, height: 60 }}
          imageStyle={{ borderRadius: 8 }}
        />
      )}
      {item.isPartner ? (
        <View
          style={{
            position: 'absolute',
            top: -4,
            right: -4,
            backgroundColor: GP.brown,
            borderRadius: 12,
            width: 24,
            height: 24,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          accessibilityLabel="Disgo Partner"
        >
          <Text style={{ color: '#fff', fontSize: 12, fontFamily: fontFamily.abhayaBold }}>D</Text>
        </View>
      ) : null}
    </View>
  );
};

/**
 * Single search result row — styled to match explore RestaurantItem.
 */
const PlaceResultRow = ({
  item,
  showDistance,
  currentLocation,
  distanceUnit,
  onPress,
  friendsAverageRatings = {},
  userCheckinHistory = [],
  getFriendsRatingBadge,
}) => {
  // Disgo rows use Convex _id; Google rows use the Place ID as `id`.
  const restaurantId = item._id || (item.isDisgoRestaurant ? item.id : null);
  const googlePlaceId =
    (typeof item.googlePlaceId === 'string' && item.googlePlaceId.trim()) ||
    (!item.isDisgoRestaurant && typeof item.id === 'string' && item.id.trim()) ||
    null;
  const hasInlineAddress = typeof item.address === 'string' && item.address.trim().length > 0;
  const { address: fetchedAddress } = useGooglePlaceDetails(
    !hasInlineAddress && googlePlaceId ? googlePlaceId : null
  );
  const { name: googleName, photoUrl: googlePhotoUrl } = useGooglePlaceDisplay(googlePlaceId);
  const displayName = googleName || item.name;
  const displayAddress = (hasInlineAddress ? item.address : fetchedAddress || '').trim();

  const distanceKm =
    item.distance != null
      ? item.distance
      : showDistance &&
          currentLocation?.latitude != null &&
          currentLocation?.longitude != null &&
          item.latitude != null &&
          item.longitude != null
        ? calculateDistance(
            currentLocation.latitude,
            currentLocation.longitude,
            item.latitude,
            item.longitude
          )
        : null;
  const distance =
    showDistance && distanceKm != null
      ? formatDistance(distanceKm, distanceUnit)
      : null;

  const cuisineLabel = item.cuisine || getPlaceTypeDisplay(item.types);
  const priceDisplay = getPriceDisplay(item);
  const hasVisited = restaurantId != null && userCheckinHistory?.includes(restaurantId);
  const friendsRating = restaurantId != null ? friendsAverageRatings?.[restaurantId] : null;
  const ratingBadge =
    friendsRating?.averageRating && typeof getFriendsRatingBadge === 'function'
      ? getFriendsRatingBadge(friendsRating.averageRating)
      : null;
  const friendsRatingText = ratingBadge
    ? ` • ${ratingBadge.emoji} ${ratingBadge.label} (${friendsRating.averageRating.toFixed(1)})`
    : '';

  return (
    <TouchableOpacity
      onPress={() => onPress(item)}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        padding: 8,
        borderWidth: 2,
        borderColor: GP.gray200,
        backgroundColor: 'transparent',
        marginBottom: 12,
        marginHorizontal: 16,
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <View style={{ position: 'relative' }}>
        <PlaceResultThumbnail item={item} photoUrl={googlePhotoUrl} />
      </View>

      <View style={{ marginLeft: 16, flex: 1, paddingRight: 8 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', flexShrink: 1 }}>
            <Text
              style={{ fontSize: 18, fontFamily: fontFamily.abhayaBold, color: '#000' }}
              numberOfLines={1}
            >
              {displayName}
            </Text>
          </View>
          {distance ? (
            <Text
              style={{
                fontSize: 12,
                fontFamily: fontFamily.abhaya,
                marginLeft: 8,
                color: GP.gray500,
              }}
            >
              {distance}
            </Text>
          ) : null}
        </View>

        <Text style={{ fontSize: 14, fontFamily: fontFamily.abhaya, color: '#000' }} numberOfLines={1}>
          {cuisineLabel}
          {priceDisplay ? ` • ${priceDisplay}` : ''}
          {friendsRatingText}
        </Text>

        {displayAddress ? (
          <Text
            style={{ fontSize: 12, fontFamily: fontFamily.abhaya, color: GP.gray500 }}
            numberOfLines={1}
          >
            {displayAddress}
          </Text>
        ) : null}

        {hasVisited ? (
          <View style={{ marginTop: 4, flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
            <View
              style={{
                backgroundColor: colors.brownMuted,
                paddingHorizontal: 8,
                paddingVertical: 2,
                borderRadius: 9999,
                marginRight: 8,
                marginBottom: 4,
              }}
              accessibilityLabel="You have visited this place"
              accessibilityRole="text"
            >
              <Text style={{ fontSize: 12, fontFamily: fontFamily.abhayaBold, color: '#fff' }}>
                Visited
              </Text>
            </View>
          </View>
        ) : null}
      </View>
    </TouchableOpacity>
  );
};

/**
 * Component to display Google Places search results
 */
const GooglePlacesSearchResults = ({
  results = [],
  isLoading = false,
  error = null,
  hasSearched = false,
  onPlaceSelect,
  onSaveToDisgo,
  onAddRestaurant,
  currentLocation = null,
  showDistance = true,
  maxResults,
  savedPlaces = new Set(), // Set of Google Place IDs that are already saved
  friendsAverageRatings = {},
  userCheckinHistory = [],
  getFriendsRatingBadge,
}) => {
  const { unit: distanceUnit } = useHousePickDistanceUnit();

  /**
   * Handle place selection
   * @param {Object} place - Selected place object
   */
  const handlePlaceSelect = async (place) => {
    let savedRestaurantId = null;

    // For Google Places (non-Disgo rows), resolve the Convex restaurant id via save mutation.
    // When the place is already in our DB (same Google Place id in savedPlaces), we must still
    // call save — it returns restaurantId for "already exists" — otherwise onPlaceSelect gets
    // no id and the map sheet errors ("No savedRestaurantId provided for Google Place selection").
    if (!place.isDisgoRestaurant && onSaveToDisgo) {
      const alreadySaved = place.id != null && savedPlaces.has(place.id);
      savedRestaurantId = await onSaveToDisgo(place, { silent: alreadySaved });
    }

    if (onPlaceSelect) {
      onPlaceSelect(place, savedRestaurantId);
    }
  };

  /**
   * Handle add restaurant action
   */
  const handleAddRestaurant = () => {
    if (onAddRestaurant) {
      onAddRestaurant();
    }
  };

  /**
   * Render loading state
   */
  const renderLoading = () => (
    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 16 }}>
      <ActivityIndicator size="small" color="#6B4C3B" />
      <Text style={{ fontSize: 14, fontFamily: fontFamily.abhaya, color: GP.gray600, marginLeft: 8 }}>
        Searching places...
      </Text>
    </View>
  );

  /**
   * Render error state
   */
  const renderError = () => (
    <View style={{ alignItems: 'center', paddingVertical: 16, paddingHorizontal: 16 }}>
      <Ionicons name="alert-circle" size={24} color="#EF4444" />
      <Text style={{ fontSize: 14, fontFamily: fontFamily.abhaya, color: GP.gray600, marginTop: 8, textAlign: 'center' }}>
        {error || 'Failed to search places'}
      </Text>
    </View>
  );

  /**
   * Render initial state before user searches
   */
  const renderInitial = () => (
    <View style={{ alignItems: 'center', paddingVertical: 16, paddingHorizontal: 16, marginTop: 8 }}>
      <Ionicons name="search-outline" size={32} color="#9CA3AF" />
      <Text style={{ fontSize: 18, fontFamily: fontFamily.abhayaBold, color: GP.gray500, marginTop: 4 }}>
        Search for restaurants
      </Text>
      <Text style={{ fontSize: 14, fontFamily: fontFamily.abhaya, color: GP.gray400, marginTop: 2, textAlign: 'center' }}>
        Type a restaurant name, cuisine, or neighborhood to get started
      </Text>
    </View>
  );

  /**
   * Render empty state
   */
  const renderEmpty = () => (
    <View style={{ alignItems: 'center', paddingVertical: 0, paddingHorizontal: 0, marginTop: 8 }}>
      <Ionicons name="search" size={32} color="#9CA3AF" />
      <Text style={{ fontSize: 18, fontFamily: fontFamily.abhayaBold, color: GP.gray500, marginTop: 0 }}>
        No places found
      </Text>
      <Text style={{ fontSize: 14, fontFamily: fontFamily.abhaya, color: GP.gray400, marginTop: 0, textAlign: 'center' }}>
        Try a different search term or check your connection
      </Text>
      <View style={{ width: '100%', marginTop: 0 }}>
        {renderAddRestaurant()}
      </View>
    </View>
  );

  /**
   * Render add restaurant section
   */
  const renderAddRestaurant = () => (
    <TouchableOpacity
      onPress={handleAddRestaurant}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        marginHorizontal: 16,
        marginVertical: 8,
        backgroundColor: '#f9fafb',
        borderWidth: 2,
        borderStyle: 'dashed',
        borderColor: GP.gray300,
        borderRadius: 12,
      }}
    >
      <Ionicons name="add-circle-outline" size={24} color="#6B4C3B" />
      <Text style={{ fontSize: 16, fontFamily: fontFamily.abhayaBold, color: GP.brown, marginLeft: 8 }}>
        Don't see the restaurant? Press here to add it!
      </Text>
    </TouchableOpacity>
  );

  if (isLoading) {
    return renderLoading();
  }

  if (error) {
    return renderError();
  }

  if (results.length === 0) {
    if (!hasSearched) {
      return renderInitial();
    }
    return renderEmpty();
  }

  const visibleResults = typeof maxResults === 'number' ? results.slice(0, maxResults) : results;

  return (
    <View style={{ paddingBottom: 30 }}>
      {visibleResults.map((item) => (
        <View key={item.id || `${item.latitude}-${item.longitude}`}>
          <PlaceResultRow
            item={item}
            showDistance={showDistance}
            currentLocation={currentLocation}
            distanceUnit={distanceUnit}
            onPress={handlePlaceSelect}
            friendsAverageRatings={friendsAverageRatings}
            userCheckinHistory={userCheckinHistory}
            getFriendsRatingBadge={getFriendsRatingBadge}
          />
        </View>
      ))}
      {renderAddRestaurant()}
    </View>
  );
};

export default GooglePlacesSearchResults;
